import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { rutaConPeriodo } from '../utils/periodoNavegacion';
import { ArrowLeftRight, BarChart3, Bell, Building2, Globe, Landmark } from 'lucide-react';
import { Select } from '../components/ui/Select';
import { useAuth } from '../context/AuthContext';
import { notificacionBalanceService } from '../services/notificacionBalanceService';
import { BalanceConsolidadoView } from '../components/admin/BalanceConsolidadoView';
import { BalanceJAView } from '../components/admin/BalanceJAView';
import { NotificacionesBalanceList } from '../components/admin/NotificacionesBalanceList';
import { DesgloseEntidadModal } from '../components/admin/DesgloseEntidadModal';
import { DeudasSection } from '../components/DeudasSection';
import { PeriodoBalance } from '../services/balanceService';
import { useProvinciasDisponibles } from '../hooks/useProvinciasDisponibles';
import { OwnerContext } from '../types/actividad';
import { ambitoRecepcionBalance, nivelPanelBalance, type AmbitoRecepcionBalance } from '../utils/permissions';

type TabId = 'jad' | 'jan' | 'jads' | 'jans' | 'jals' | 'deudas' | 'notificaciones';

function resolverTabDestino(tipoEntidad: 'lumisial' | 'diocesis', isDiocesano: boolean): TabId {
  if (tipoEntidad === 'lumisial') return 'jals';
  if (isDiocesano) return 'jad';
  return 'jads';
}

function resolverOwnerContextJADs(isNacional: boolean, isGlobal: boolean, provinciaId: string | null | undefined): OwnerContext | null {
  if (isNacional && provinciaId) return { tipo: 'provincia', id: provinciaId, nombre: '' };
  if (isGlobal) return { tipo: 'global', id: 'global', nombre: '' };
  return null;
}

export const AdminBalancePage: React.FC = () => {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // La JAD y la JAN usan este panel igual que su Junta Sacerdotal: su tab propia es operable
  // y las subordinadas son de lectura para el dinero ordinario.
  const nivelPanel = nivelPanelBalance(usuario);
  const isDiocesano = nivelPanel === 'diocesano';
  const isNacional = nivelPanel === 'nacional';
  const isGlobal = nivelPanel === 'global';

  // Tabs y tab inicial según rol
  let tabs: TabId[];
  let defaultTab: TabId;
  if (isDiocesano) {
    tabs = ['jad', 'jals', 'deudas', 'notificaciones'];
    defaultTab = 'jad';
  } else if (isNacional) {
    tabs = ['jan', 'jads', 'jals', 'deudas', 'notificaciones'];
    defaultTab = 'jan';
  } else {
    tabs = ['jans', 'jads', 'jals', 'deudas', 'notificaciones'];
    defaultTab = 'jans';
  }

  const tabParam = searchParams.get('tab') as TabId | null;
  const mesParam = searchParams.get('mes');
  const añoParam = searchParams.get('anio');
  const initialMes = mesParam ? Number.parseInt(mesParam, 10) : undefined;
  const initialAño = añoParam ? Number.parseInt(añoParam, 10) : undefined;

  const [activeTab, setActiveTab] = useState<TabId>(
    tabParam && tabs.includes(tabParam) ? tabParam : defaultTab
  );
  const [pendingCount, setPendingCount] = useState(0);

  // Hook para provincias (solo admin_global)
  const {
    provincias: provinciasDisponibles,
    provinciaSeleccionada,
    setProvinciaSeleccionada,
  } = useProvinciasDisponibles();

  const [desgloseModal, setDesgloseModal] = useState<{
    isOpen: boolean;
    entidadId: string;
    entidadNombre: string;
    tipoEntidad: 'lumisial' | 'diocesis';
    periodo: PeriodoBalance;
    mostrarTodos: boolean;
  }>({
    isOpen: false,
    entidadId: '',
    entidadNombre: '',
    tipoEntidad: 'lumisial',
    periodo: { tipo: 'mensual', año: new Date().getFullYear(), mes: new Date().getMonth() + 1 },
    mostrarTodos: false,
  });

  // Sube tras recibir u observar una rendición para refrescar el contador al instante.
  const [refrescoPendientes, setRefrescoPendientes] = useState(0);
  const claveRecepcion = JSON.stringify(ambitoRecepcionBalance(usuario, provinciaSeleccionada));

  useEffect(() => {
    const ambito = JSON.parse(claveRecepcion) as AmbitoRecepcionBalance | null;
    if (!ambito) return;
    const contar = () => {
      notificacionBalanceService.contarPendientes(ambito)
        .then(setPendingCount)
        .catch((error: unknown) => { console.error('Error loading pending count:', error); });
    };
    contar();
    const interval = setInterval(contar, 30000);
    return () => clearInterval(interval);
  }, [claveRecepcion, refrescoPendientes]);

  const handleVerDesglose = (
    entidadId: string,
    tipoEntidad: 'lumisial' | 'diocesis',
    periodo: PeriodoBalance,
    entidadNombre: string = '',
    mostrarTodos: boolean = false
  ) => {
    setDesgloseModal({ isOpen: true, entidadId, entidadNombre, tipoEntidad, periodo, mostrarTodos });
  };

  const handleCloseDesglose = () => {
    setDesgloseModal({ ...desgloseModal, isOpen: false });
  };

  const [balanceNavPeriodo, setBalanceNavPeriodo] = useState<{ mes: number; año: number } | null>(null);

  const handleVerBalance = (tipoEntidad: 'lumisial' | 'diocesis', periodo: PeriodoBalance) => {
    const mes = periodo.tipo === 'mensual' ? periodo.mes : new Date().getMonth() + 1;
    const año = periodo.año;
    setBalanceNavPeriodo({ mes: mes ?? new Date().getMonth() + 1, año });
    setActiveTab(resolverTabDestino(tipoEntidad, isDiocesano));
  };

  if (!usuario || !nivelPanel) {
    return (
      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-8 text-center">
        <p className="text-slate-600">No tienes permisos para acceder a este módulo.</p>
      </div>
    );
  }

  // Construir ownerContext para tabs JAD/JAN propio
  const ownerContextJAD: OwnerContext | null =
    isDiocesano && usuario.diocesis_id
      ? { tipo: 'diocesis', id: usuario.diocesis_id, nombre: '' }
      : null;

  const ownerContextJAN: OwnerContext | null =
    isNacional && usuario.provincia_eclesiastica_id
      ? { tipo: 'provincia', id: usuario.provincia_eclesiastica_id, nombre: '' }
      : null;

  // Tab JADs (agrupado por provincia) — admin_nacional ve JADs de su provincia; admin_global ve todos
  const ownerContextJADs = resolverOwnerContextJADs(isNacional, isGlobal, usuario.provincia_eclesiastica_id);

  // Tab JANs globales — admin_global
  const ownerContextJANs: OwnerContext = { tipo: 'global', id: 'global', nombre: '' };

  // Etiquetas de tabs
  const tabLabel: Record<TabId, string> = {
    jad: 'JAD',
    jan: 'JAN',
    jads: 'JADs',
    jans: 'JANs',
    jals: 'JALs',
    deudas: 'Deudas',
    notificaciones: 'Notificaciones',
  };

  // Owner para la tab Deudas: entidad propia por rol; admin_global no tiene entidad propia (solo lectura).
  const ownerDeudas: OwnerContext | null = ownerContextJAD ?? ownerContextJAN ?? null;

  // Período resuelto para pasar a los componentes hijos.
  // La key incluye los params de URL para forzar remount cuando llega una
  // navegación externa (Bell), sin afectar cambios manuales dentro del componente.
  const resMes = balanceNavPeriodo?.mes ?? initialMes;
  const resAño = balanceNavPeriodo?.año ?? initialAño;
  const periodKey = `${mesParam ?? ''}-${añoParam ?? ''}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Balance Consolidado</h2>
          <p className="text-slate-600">
            {isDiocesano
              ? 'Balance de lumisiales de tu diócesis'
              : 'Balance de diócesis de tu provincia eclesiástica'}
          </p>
        </div>
      </div>

      {(isDiocesano || isNacional) && (
        <div className="flex">
          <button
            type="button"
            onClick={() => { navigate(rutaConPeriodo(`/admin/balance/movimientos?volverA=${activeTab}`, searchParams.toString())); }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 hover:border-slate-300 transition-colors"
          >
            <ArrowLeftRight className="w-4 h-4" />
            Ver movimientos
          </button>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-md border border-slate-200 overflow-hidden">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              className={`shrink-0 sm:flex-1 flex items-center justify-center gap-1.5 px-3 sm:px-6 py-3 sm:py-4 text-sm sm:text-base font-semibold transition-colors whitespace-nowrap relative ${
                activeTab === tab
                  ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-700'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {tab === 'jals' && <BarChart3 className="w-4 h-4 sm:w-5 sm:h-5" />}
              {(tab === 'jad' || tab === 'jan') && <Building2 className="w-4 h-4 sm:w-5 sm:h-5" />}
              {(tab === 'jads' || tab === 'jans') && <Globe className="w-4 h-4 sm:w-5 sm:h-5" />}
              {tab === 'deudas' && <Landmark className="w-4 h-4 sm:w-5 sm:h-5" />}
              {tab === 'notificaciones' && <Bell className="w-4 h-4 sm:w-5 sm:h-5" />}
              {tabLabel[tab]}
              {tab === 'notificaciones' && pendingCount > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full min-w-[1.25rem] h-5 flex items-center justify-center font-bold px-1">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* Tab JAD propio (admin_diocesano) */}
          {activeTab === 'jad' && ownerContextJAD && (
            <BalanceJAView
              key={`jad-${periodKey}`}
              ownerContext={ownerContextJAD}
              modo="propio"
              initialMes={resMes}
              initialAño={resAño}
            />
          )}

          {/* Tab JAN propio (admin_nacional) */}
          {activeTab === 'jan' && ownerContextJAN && (
            <BalanceJAView
              key={`jan-${periodKey}`}
              ownerContext={ownerContextJAN}
              modo="propio"
              initialMes={resMes}
              initialAño={resAño}
            />
          )}

          {/* Tab JADs agrupado (admin_nacional ve JADs por diócesis de su provincia) */}
          {activeTab === 'jads' && ownerContextJADs && (
            <BalanceJAView
              key={`jads-${periodKey}`}
              ownerContext={ownerContextJADs}
              modo="agrupado"
              initialMes={resMes}
              initialAño={resAño}
            />
          )}

          {/* Tab JANs global (admin_global ve todas las JANs) */}
          {activeTab === 'jans' && (
            <BalanceJAView
              key={`jans-${periodKey}`}
              ownerContext={ownerContextJANs}
              initialMes={resMes}
              initialAño={resAño}
            />
          )}

          {/* Tab JALs: balance consolidado de lumisiales */}
          {activeTab === 'jals' && (
            <BalanceConsolidadoView
              key={`jals-${periodKey}`}
              onVerDesglose={handleVerDesglose}
              initialMes={resMes}
              initialAño={resAño}
            />
          )}

          {/* Tab Deudas: propias (gestión) + subordinadas (solo lectura) */}
          {activeTab === 'deudas' && (
            <DeudasSection owner={ownerDeudas} soloLectura={isGlobal} />
          )}

          {/* Tab Notificaciones */}
          {activeTab === 'notificaciones' && (
            <>
              {/* Selector de provincia para admin_global */}
              {isGlobal && (
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-4">
                  <Select
                    label="Provincia Eclesiástica"
                    options={provinciasDisponibles.map((p) => ({ value: p.id, label: p.nombre }))}
                    value={provinciaSeleccionada}
                    onChange={(v) => setProvinciaSeleccionada(v)}
                    placeholder={provinciasDisponibles.length === 0 ? 'Cargando...' : 'Seleccionar...'}
                    disabled={provinciasDisponibles.length === 0}
                  />
                </div>
              )}

              <NotificacionesBalanceList
                onVerDesglose={handleVerDesglose}
                onVerBalance={handleVerBalance}
                onUpdate={() => { setRefrescoPendientes(n => n + 1); }}
                provinciaSeleccionada={provinciaSeleccionada}
              />
            </>
          )}
        </div>
      </div>

      <DesgloseEntidadModal
        isOpen={desgloseModal.isOpen}
        onClose={handleCloseDesglose}
        entidadId={desgloseModal.entidadId}
        entidadNombre={desgloseModal.entidadNombre}
        tipoEntidad={desgloseModal.tipoEntidad}
        periodo={desgloseModal.periodo}
        mostrarTodosMovimientos={desgloseModal.mostrarTodos}
      />
    </div>
  );
};
